import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Alert, CircularProgress, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Accordion,
  AccordionSummary, AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import EditarSolicitudDialog from "../components/EditarSolicitudDialog";
import EmitirVacacionDialog from "../../administracionPersonal/components/EmitirVacacionDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const ESTADO_COLOR = { Espera: "warning", Emitido: "default", Aceptado: "success", Rechazado: "error" };
const colorEstado = (estado) => ESTADO_COLOR[estado] || "error";

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const nombreCompleto = (r) => [r.nombre, r.paterno, r.materno].filter(Boolean).join(" ");

const SolicitudesPermisoAdminPage = () => {
  const [soyAprobador, setSoyAprobador] = useState(null); // null = todavía no se sabe
  const [pendientes, setPendientes] = useState([]);
  const [aceptadas, setAceptadas] = useState([]);
  const [procesadas, setProcesadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [accionDialog, setAccionDialog] = useState(null); // { row, tipo: 'Aceptado' | 'Rechazado' }
  const [obsProceso, setObsProceso] = useState("");
  const [procesando, setProcesando] = useState(false);

  const [editarRow, setEditarRow] = useState(null);
  const [procesarRow, setProcesarRow] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get("/api/permisos/soy-aprobador")
      .then(({ data }) => {
        const permitido = Boolean(data?.data?.esAprobador);
        setSoyAprobador(permitido);
        if (!permitido) return null;
        return Promise.all([
          axios.get("/api/permisos/pendientes"),
          axios.get("/api/permisos/aceptadas"),
          axios.get("/api/permisos/procesadas"),
        ]).then(([resPend, resAcept, resProc]) => {
          setPendientes(resPend.data?.data || []);
          setAceptadas(resAcept.data?.data || []);
          setProcesadas(resProc.data?.data || []);
        });
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar las solicitudes."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(cargar, [cargar]);

  const abrirAccion = (row, tipo) => {
    setAccionDialog({ row, tipo });
    setObsProceso("");
  };

  const confirmarAccion = async () => {
    if (!accionDialog) return;
    setProcesando(true);
    try {
      await axios.patch(`/api/permisos/${accionDialog.row.id}/procesar`, {
        estado: accionDialog.tipo,
        obs_proceso: obsProceso.trim(),
      });
      setAccionDialog(null);
      cargar();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al procesar la solicitud.");
    } finally {
      setProcesando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Solicitudes de Permiso"
        subtitle="Aprobación de vacaciones y permisos"
        showBack={false}
      />
      <Box sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : soyAprobador === false ? (
          <Alert severity="warning">No tienes permiso para ver esta página.</Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Pendientes ({pendientes.length})
            </Typography>
            {pendientes.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>No hay solicitudes pendientes en tu sucursal.</Alert>
            ) : (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>ID</Th>
                        <Th>Razón</Th>
                        <Th>Fecha inicio</Th>
                        <Th>Fecha fin</Th>
                        <Th align="center">Días</Th>
                        <Th>Observación</Th>
                        <Th>Solicitante</Th>
                        <Th align="center">Acción</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendientes.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.razon}</TableCell>
                          <TableCell>{formatFecha(row.fecha_ini)}</TableCell>
                          <TableCell>{formatFecha(row.fecha_fin)}</TableCell>
                          <TableCell align="center">{row.dias}</TableCell>
                          <TableCell>{row.observacion || "-"}</TableCell>
                          <TableCell>{row.usu}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Button
                                size="small" variant="outlined"
                                startIcon={<EditIcon fontSize="small" />}
                                onClick={() => setEditarRow(row)}
                              >
                                Editar
                              </Button>
                              <Button
                                size="small" color="success" variant="outlined"
                                startIcon={<CheckIcon fontSize="small" />}
                                onClick={() => abrirAccion(row, "Aceptado")}
                              >
                                Aprobar
                              </Button>
                              <Button
                                size="small" color="error" variant="outlined"
                                startIcon={<CloseIcon fontSize="small" />}
                                onClick={() => abrirAccion(row, "Rechazado")}
                              >
                                Rechazar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Aprobadas — Por procesar ({aceptadas.length})
            </Typography>
            {aceptadas.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>No hay solicitudes aprobadas pendientes de procesar.</Alert>
            ) : (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>ID</Th>
                        <Th>Razón</Th>
                        <Th>Fecha inicio</Th>
                        <Th>Fecha fin</Th>
                        <Th align="center">Días</Th>
                        <Th>Solicitante</Th>
                        <Th>Observación</Th>
                        <Th align="center">Acción</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aceptadas.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.razon}</TableCell>
                          <TableCell>{formatFecha(row.fecha_ini)}</TableCell>
                          <TableCell>{formatFecha(row.fecha_fin)}</TableCell>
                          <TableCell align="center">{row.dias}</TableCell>
                          <TableCell>{row.usu}</TableCell>
                          <TableCell>{row.observacion || "-"}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Button
                                size="small" variant="outlined"
                                startIcon={<EditIcon fontSize="small" />}
                                onClick={() => setEditarRow(row)}
                              >
                                Editar
                              </Button>
                              <Button
                                size="small" color="primary" variant="outlined"
                                startIcon={<AssignmentTurnedInIcon fontSize="small" />}
                                onClick={() => setProcesarRow(row)}
                              >
                                Procesar permiso
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Accordion variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Procesadas recientemente ({procesadas.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {procesadas.length === 0 ? (
                  <Alert severity="info" sx={{ m: 1 }}>Todavía no hay solicitudes procesadas.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                      <TableHead>
                        <TableRow>
                          <Th>ID</Th>
                          <Th>Razón</Th>
                          <Th>Fecha inicio</Th>
                          <Th>Fecha fin</Th>
                          <Th align="center">Días</Th>
                          <Th>Solicitante</Th>
                          <Th>Observación proceso</Th>
                          <Th align="center">Estado</Th>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {procesadas.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.razon}</TableCell>
                            <TableCell>{formatFecha(row.fecha_ini)}</TableCell>
                            <TableCell>{formatFecha(row.fecha_fin)}</TableCell>
                            <TableCell align="center">{row.dias}</TableCell>
                            <TableCell>{row.usu}</TableCell>
                            <TableCell>{row.obs_proceso || "-"}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.estado}
                                size="small"
                                color={colorEstado(row.estado)}
                                sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>

      <Dialog open={Boolean(accionDialog)} onClose={() => (procesando ? null : setAccionDialog(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {accionDialog?.tipo === "Aceptado" ? "Aprobar solicitud" : "Rechazar solicitud"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              <strong>Razón:</strong> {accionDialog?.row?.razon}<br />
              <strong>Solicitante:</strong> {accionDialog?.row?.usu}<br />
              <strong>Detalle:</strong> {accionDialog?.row?.observacion || "-"}
            </Typography>
            <TextField
              size="small" label="Observación" multiline minRows={3}
              value={obsProceso} onChange={(e) => setObsProceso(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccionDialog(null)} disabled={procesando}>Cancelar</Button>
          <Button
            variant="contained"
            color={accionDialog?.tipo === "Aceptado" ? "success" : "error"}
            onClick={confirmarAccion}
            disabled={procesando}
          >
            {procesando ? "Guardando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <EditarSolicitudDialog
        open={Boolean(editarRow)}
        solicitud={editarRow}
        onClose={() => setEditarRow(null)}
        onGuardado={() => {
          setEditarRow(null);
          cargar();
        }}
      />

      <EmitirVacacionDialog
        open={Boolean(procesarRow)}
        empleado={procesarRow ? { id: procesarRow.id_empleado, nombre: nombreCompleto(procesarRow), cargo: procesarRow.cargo } : null}
        solicitud={procesarRow}
        onClose={() => setProcesarRow(null)}
        onEmitido={() => {
          setProcesarRow(null);
          cargar();
        }}
      />
    </DashboardLayout>
  );
};

export default SolicitudesPermisoAdminPage;
